import { Player } from './player';

export class SocialGroup {
  private _id: number;
  private center: { x: number; y: number };
  private _isPrimaryGroup = false;
  private members: Map<string, Player> = new Map<string, Player>();

  public constructor(id: number, center?: { x: number; y: number }) {
    this._id = id;
    this.center = center ?? { x: 0, y: 0 };
  }

  public get id(): number {
    return this._id;
  }

  public get isPrimaryGroup(): boolean {
    return this._isPrimaryGroup;
  }

  /**
   * Add a player to the group.
   * @param player The player to add to the group
   * @param limitExceeded If the player exceeds the group limit.
   */
  public add(player: Player, limitExceeded: boolean, softLimitExceeded?: boolean, hardLimitExceeded?: boolean): void {
    if (player.isPrimaryPlayer) {
      this._isPrimaryGroup = true;
      player.groupSoftLimitExceeded = !!softLimitExceeded;
      player.groupHardLimitExceeded = !!hardLimitExceeded;
    }

    player.groupWarning = limitExceeded;

    if (!player.inSag && player.isGroupSag) {
      player.groupWarning = true;
    }

    this.members.set(player.userId, player);
    this.refreshGroup();
  }

  /**
   * Remove a player from the group.
   * @param player The player to remove
   */
  public remove(player: Player): void {
    this.members.delete(player.userId);
    if (player.isPrimaryPlayer) {
      this._isPrimaryGroup = false;
    }

    this.refreshGroup();
  }

  /**
   * Refresh the group's state.
   *
   * This will update if any members have groupWarnings.
   */
  public refreshGroup(): void {
    this.membersArray.forEach((member) => {
      member.toggleGroup({
        isInGroup: this.size > 1,
        groupId: this._id,
        isPrimaryGroup: this._isPrimaryGroup,
        warning: member.groupWarning,
      });
    });
  }

  public setCenter(x: number, y: number): void {
    this.center.x = x;
    this.center.y = y;
  }

  public get size(): number {
    return this.members.size;
  }

  public get x(): number {
    return this.center.x;
  }

  public get y(): number {
    return this.center.y;
  }

  public get membersArray(): Player[] {
    return Array.from(this.members.values());
  }

  /**
   * Dump the group to json-serializable object
   * @returns An object representing the group
   */

  public toObject(): Record<string, unknown> {
    const members = this.membersArray.map((m) => m.userId);
    return {
      clusterId: this._id,
      players: members,
      center: this.center,
    };
  }

  public get isEmpty(): boolean {
    return this.members.size === 0;
  }
}
